import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Title from '../../component/Title'
import API from '../../component/Axios'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'

const CATEGORY_VISIBILITY_BUTTON_SX = {
    height: 28,
    minWidth: 64,
    px: 1.25,
    borderRadius: '4px',
    boxShadow: 'none',
    fontWeight: 700,
    '& .MuiButton-startIcon': {
        mr: 0.5,
    },
}

const APP_GIFTICON_CATEGORY_TABLE_COLUMNS = [
    { width: 110 },
    { width: 260 },
    { width: 140 },
    { width: 140 },
]

const APP_GIFTICON_CATEGORY_TABLE_MIN_WIDTH = APP_GIFTICON_CATEGORY_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const APP_GIFTICON_CATEGORY_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const APP_GIFTICON_CATEGORY_ACTION_CELL_SX = {
    whiteSpace: 'nowrap',
}

export default function AppGifticonCategorys() {
    const location = useLocation()
    const history = useHistory()
    const actionInFlightRef = useRef(new Set())
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: {
            searchStatus: '',
        },
        selectParams: {
            searchStatus: 'searchStatus',
        },
    }))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [lastSort, setLastSort] = useState(0)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const curPage = initialListState.curPage
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const searchText = initialListState.searchText
    const searchSelect = initialListState.searchSelect
    const searchStatus = searchSelect.searchStatus
    
    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appGifticonCategory/getList', { 
                page: curPage, offset: offset, searchText: searchText, searchSelect: { searchStatus }
            })
            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setLastSort(data.data.lastSort || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '앱 기프티콘 카테고리 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setLastSort(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '앱 기프티콘 카테고리 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setLastSort(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchStatus, searchText])

    useEffect(() => {
        getData()
    }, [getData])

    // 페이지와 레거시 필터는 query로 정리해 새로고침 후에도 유지한다.
    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery: searchText,
            searchSelect,
            selectParams: {
                searchStatus: 'searchStatus',
            },
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [curPage, history, location.pathname, location.search, location.state, searchSelect, searchText])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const isBusy = Boolean(actionLoading)

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="기프티콘 카테고리 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || isBusy}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 기프티콘 카테고리가 없습니다."
                description="기프티콘 카테고리가 등록되면 이 목록에 표시됩니다."
            />
        )
    }

    const runAction = async ({ key, endpoint, payload, successMessage }) => {
        if (actionInFlightRef.current.has(key) || isBusy) {
            return
        }

        try {
            actionInFlightRef.current.add(key)
            setActionLoading(key)

            const { data } = await API.post(endpoint, payload)
            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(successMessage || data.message)
                await getData(false)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('앱 기프티콘 카테고리 상태를 변경하지 못했습니다.')
        } finally {
            actionInFlightRef.current.delete(key)
            setActionLoading(null)
        }
    }

    const handleSortUpdate = async (goodsTypeDtlNm, sort, type) => {
        if (sort === 99) return

        await runAction({
            key: `sort:${goodsTypeDtlNm}:${type}`,
            endpoint: '/api/v1/admin/appGifticonCategory/sortUpdate',
            payload: { goodsTypeDtlNm, sort, type }
        })
    }

    const handleVisibleUpdate = async (goodsTypeDtlNm, sort, type) => {
        await runAction({
            key: `visible:${goodsTypeDtlNm}:${type}`,
            endpoint: '/api/v1/admin/appGifticonCategory/visibleUpdate',
            payload: { goodsTypeDtlNm, sort, type }
        })
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 5, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>APP 기프티콘 카테고리 관리</Title>

                    {/* 테이블 */}
                    <ListTableContainer sx={{ opacity: isBusy ? 0.6 : 1, pointerEvents: isBusy ? 'none' : 'auto' }}>
                        <Table size="small" sx={{ minWidth: APP_GIFTICON_CATEGORY_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {APP_GIFTICON_CATEGORY_TABLE_COLUMNS.map((column, index) => (
                                    <col key={`app-gifticon-category-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>노출순서</TableCell>
                                    <TableCell align="center">카테고리명</TableCell>
                                    <TableCell align="center">순서변경</TableCell>
                                    <TableCell align="right">노출여부</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.goodsTypeDtlNm}>
                                            <TableCell>{row.sort !== 99 ? row.sort : '-'}</TableCell>
                                            <TableCell align="center" sx={APP_GIFTICON_CATEGORY_TEXT_CELL_SX}>{row.goodsTypeDtlNm || '-'}</TableCell>
                                            <TableCell align="center" sx={APP_GIFTICON_CATEGORY_ACTION_CELL_SX}>
                                                {row.sort !== 99 && row.sort > 1 && <IconButton onClick={() => handleSortUpdate(row.goodsTypeDtlNm, row.sort, 'up')} disabled={isBusy}><ArrowUpward /></IconButton>}
                                                {row.sort !== 99 && row.sort < 10 && lastSort !== row.sort && <IconButton onClick={() => handleSortUpdate(row.goodsTypeDtlNm, row.sort, 'down')} disabled={isBusy}><ArrowDownward /></IconButton>}
                                            </TableCell>
                                            <TableCell align="right" sx={APP_GIFTICON_CATEGORY_ACTION_CELL_SX}>
                                                {row.sort === 99 ?
                                                    lastSort < 10 && (
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            startIcon={<AddIcon />}
                                                            size="small"
                                                            sx={CATEGORY_VISIBILITY_BUTTON_SX}
                                                            onClick={() => handleVisibleUpdate(row.goodsTypeDtlNm, row.sort, 'add')}
                                                            disabled={isBusy}
                                                        >
                                                            추가
                                                        </Button>
                                                    )
                                                :
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        startIcon={<RemoveIcon />}
                                                        size="small"
                                                        sx={CATEGORY_VISIBILITY_BUTTON_SX}
                                                        onClick={() => handleVisibleUpdate(row.goodsTypeDtlNm, row.sort, 'remove')}
                                                        disabled={isBusy}
                                                    >
                                                        제거
                                                    </Button>
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {/* {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />} */}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
